// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  RatingType,
} from 'common.data';
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import moment from 'moment';
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  getRegionByRegionSeasonId,
} from 'common.helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  replaceObjectCommasToDots,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import RatingFields from '../../fields/RatingFields';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate & $WithRouter;

export type $StateProps = {
  auth: $ReducerState<'auth'>;
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Rating'>>;
  loading: boolean;
  mainItem: ($Model<'RegattaEntry'> & {
    Regatta: $Model<'Regatta'>;
  }) | null;
};

const pageId = adminPages.regattaEntryRatingsPage;

class RegattaEntryRatingsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      mainItem: null,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          mainItem,
        } = this.state;

        if (mainItem !== null && data.modelId === mainItem.id && data.modelName === 'RegattaEntry') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      params,
    } = this.props;

    const {
      RegattaEntryId,
    } = params;

    await defaultGetData({
      action: async () => {
        if (RegattaEntryId) {
          await this.setStateAsync({
            loading: true,
          });

          const mainItem = await Services.Api.getOneRegattaEntry({
            id: RegattaEntryId,
          });
          const data = await Services.Api.getManyRegattaEntryRating({
            RegattaEntryId,
          });

          await this.setStateAsync({
            data,
            loading: false,
            mainItem,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  isSuperAdmin(): boolean {
    const {
      auth,
    } = this.props;

    return auth.user !== null && auth.user.Super === true;
  }

  async addLatestRegattaEntryRatings(): Promise<void> {
    const {
      mainItem,
    } = this.state;

    if (mainItem !== null) {
      const {
        EntryId,
        id,
      } = mainItem;

      await defaultGetData({
        action: async () => {
          if (EntryId !== null) {
            await Services.Api.addLatestRegattaEntryRatings({
              EntryId,
              id,
            });
          }
        },
      });
    }
  }

  renderSyncRatingsButton() {
    const {
      t,
    } = this.props;
    const {
      mainItem,
    } = this.state;

    if (mainItem !== null && mainItem.EntryId !== null) {
      return (
        <Button
          className={'mr-1'}
          color={'secondary'}
          id={'sync-ratings-button'}
          onClick={() => this.addLatestRegattaEntryRatings()}
          size={'sm'}
        >
          {t('Sync to latest ratings')}
        </Button>
      );
    }

    return null;
  }

  renderHeader() {
    const {
      main,
      redirect,
      t,
    } = this.props;
    const {
      mainItem,
    } = this.state;

    if (mainItem !== null) {
      const newItem: $ModelUpsertParams<'Rating'> = {
        File: null,
        Latest: true,
        ReferenceId: null,
        RegionId: getRegionByRegionSeasonId({
          regions: main.regions,
          RegionSeasonId: mainItem.Regatta.RegionSeasonId,
        }).id,
        RegistrationNo: mainItem.RegistrationNo,
        Type: RatingType.monotype,
        Uid: String(moment().unix()),
        Values: {
        },
      };

      return (
        <Card id={adminSections[pageId].header}>
          <SectionHeader
            description={mainItem !== null ? `${mainItem.RegistrationNo} ${mainItem.Name || ''}` : ''}
            rightButtons={(
              <>
                {this.renderSyncRatingsButton()}
                <FormDialog
                  fields={(
                    <RatingFields item={newItem} />
                  )}
                  id={'create-regatta-entry-rating-dialog'}
                  labelCloseButton={t('Close')}
                  labelSaveButton={t('Save')}
                  onAction={(values) => defaultUpsertItem({
                    action: async () => {
                      const params = cleanFormValues<$Model<'Rating'>>({
                        ...newItem,
                        ...values,
                        Values: replaceObjectCommasToDots(values.Values as Record<string, string>),
                      });

                      const response = await Services.Api.createRating(params);

                      await Services.Api.addRegattaEntryRating({
                        RatingId: response.id,
                        RegattaEntryId: mainItem.id,
                      });
                    },
                  })}
                  subTitle={t('New')}
                  title={t('Regatta entry rating')}
                >
                  <Button
                    color={'info'}
                    id={'create-regatta-entry-rating-button'}
                    size={'sm'}
                    title={t('Add new regatta entry rating')}
                  >
                    {t('Add new')}
                  </Button>
                </FormDialog>
              </>
            )}
            title={t('Regatta entry ratings')}
            titleButton={(
              <Button
                className={'mr-2'}
                color={'secondary'}
                id={'redirect-regatta-entries-page-button'}
                onClick={() => redirect({
                  pathname: '/regatta/entries',
                })}
                size={'sm'}
              >
                <em className={'fa icon-arrow-left'} />
              </Button>
            )}
          />
        </Card>
      );
    }

    return null;
  }

  renderButtons(dataItem: $Model<'Rating'>) {
    const {
      main,
      t,
    } = this.props;
    const {
      mainItem,
    } = this.state;

    if (mainItem !== null) {
      const successorItem = {
        File: null,
        Latest: true,
        ReferenceId: dataItem.id,
        RegionId: getRegionByRegionSeasonId({
          regions: main.regions,
          RegionSeasonId: mainItem.Regatta.RegionSeasonId,
        }).id,
        RegistrationNo: mainItem.RegistrationNo,
        Type: dataItem.Type,
        Uid: String(moment().unix()),
        Values: dataItem.Values,
      };

      const entryName: string = mainItem !== null ? `${mainItem.RegistrationNo} ${mainItem.Name || ''}` : '';

      return (
        <div className={'d-flex justify-content-end'}>
          <FormDialog
            fields={(
              <RatingFields item={successorItem} />
            )}
            id={`update-regatta-entry-rating-dialog-${dataItem.id}`}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'Rating'>>({
                  ...successorItem,
                  ...values,
                  Values: replaceObjectCommasToDots(values.Values as Record<string, string>),
                });

                const response = await Services.Api.createRating(params);

                await Services.Api.addRegattaEntryRating({
                  RatingId: response.id,
                  RegattaEntryId: mainItem.id,
                });
              },
            })}
            subTitle={`${dataItem.Type}: ${entryName}`}
            title={t('Regatta entry rating')}
          >
            <Button
              color={'warning'}
              id={`update-regatta-entry-rating-button-${dataItem.id}`}
              outline
              size={'sm'}
            >
              {t('Edit')}
            </Button>
          </FormDialog>
          {this.isSuperAdmin() === true && (
            <DeleteDialog
              content={t('Do you really want to delete?')}
              id={`delete-regatta-entry-rating-dialog-${dataItem.id}`}
              labelCloseButton={t('Close')}
              labelDeleteButton={t('Delete')}
              onAction={() => defaultDeleteItem({
                action: async () => Services.Api.removeRegattaEntryRating({
                  RatingId: dataItem.id,
                  RegattaEntryId: mainItem.id,
                }),
              })}
              subTitle={`${dataItem.Type}: ${entryName}`}
              title={t('Regatta entry rating')}
            >
              <Button
                className={'ml-1'}
                color={'danger'}
                id={`delete-regatta-entry-rating-button-${dataItem.id}`}
                outline
                size={'sm'}
              >
                {t('Delete')}
              </Button>
            </DeleteDialog>
          )}
        </div>
      );
    }

    return null;
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Type')}</th>
            <th>{t('Value')}</th>
            <th>{t('Date')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'Rating'>) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Type}</td>
              <td>{`${dataItem.KeyValueName}: ${dataItem.KeyValue || ''}`}</td>
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default RegattaEntryRatingsPageView;
